import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Button, Icon } from '@seeqdev/qomponents';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { SearchResult } from '@/search/SearchResult.molecule';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { canModifyWorkbook } from '@/services/authorization.service';
import { sqLicenseManagementStore, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import { LIMIT_IN_OVERVIEW, SEARCH_RESULT_TYPES } from '@/search/search.constants';
import { clearSelection, getStoreForPane, setMode } from '@/search/search.actions';
import {
  setDisplayAssetGroupEditor,
  setDisplayItemTableEditor,
  setDisplayTableDefinitionEditor,
} from '@/worksheet/worksheet.actions';
import { LOAD_STATUS } from '@/workbook/workbook.constants';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { doTrack } from '@/track/track.service';
import type { SelectedSearchItem } from '@/search/search.types';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setControlPane, setEnableTreeCreation } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { CONTROL_PANE_DISPLAY } from '@/tableDefinitionEditor/tableDefinition.constants';
import { CreateModelButton } from '@/tools/models/CreateModelButton.molecule';

export interface SearchGroupProps {
  loadStatus: keyof typeof LOAD_STATUS;
  label: string;
  mode: SearchModes;
  searchMode: SearchModes;
  searchTypes: string[];
  scopeIds: string[];
  restrictExploration: boolean;
  onErrorClick: () => void;
  onClickItem: (item: SelectedSearchItem) => void;
  items: any[];
  pane: SearchPanes;
  indicateSelection?: boolean;
  selectedItemId?: string;
  searchResultIcons: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  noItemsMessage?: string;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  totalItemsCount?: number;
  onSetModeIconClick?: () => void;
  showHomeIcon?: boolean;
  onHomeIconClick?: () => void;
  hideCreateButton?: boolean;
  showPreviewBadge?: boolean;
}

export const SearchGroup: React.FunctionComponent<SearchGroupProps> = ({
  items,
  loadStatus,
  label,
  mode,
  pane,
  onErrorClick,
  onClickItem,
  searchResultIcons,
  searchMode,
  searchTypes,
  scopeIds,
  restrictExploration,
  noItemsMessage,
  indicateSelection = false,
  selectedItemId,
  isSelectingAsset = false,
  predefinedSearchTypes,
  totalItemsCount = 0,
  onSetModeIconClick,
  showHomeIcon = false,
  onHomeIconClick,
  showPreviewBadge = false,
  hideCreateButton = false,
}) => {
  const { t } = useTranslation();
  const displayAssetGroupEditor = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayAssetGroupEditor);
  const displayScalingTableEditor = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayTableDefinitionView);
  const displayItemTableView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayItemTableView);

  const store = getStoreForPane(pane);
  const selectedItemIds = useFluxPath(store, () => store.selectedItemIds);
  const selectedItemTable = useFluxPath(store, () => store.selectedItemTable);
  const showCreateAssetGroupButton =
    searchMode === 'asset-groups' &&
    sqLicenseManagementStore.hasAssetGroups() &&
    pane === 'main' &&
    canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateScalingTableButton =
    searchMode === 'scaling-tables' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateModelButton =
    searchMode === 'models' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const showItemTableButton =
    searchMode === 'item-tables' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook);
  const displayHierarchyView = searchMode === 'item-hierarchies' && pane === 'main' && !!selectedItemTable;
  const showCreateHierarchyButton = displayHierarchyView && canModifyWorkbook(sqWorkbookStore.workbook);
  const searchingUsages = mode === 'usages';

  const setModeClick = () =>
    onSetModeIconClick ? onSetModeIconClick() : setMode(pane, searchMode, searchTypes, restrictExploration, scopeIds);

  const renderSetModeIconButton = (testId?: string) => {
    return (
      <Icon
        testId={testId ?? 'setModeIconButton'}
        icon="fa-chevron-right"
        extraClassNames="text-interactive pl5 pr5"
        onClick={() => setModeClick()}
      />
    );
  };

  const renderAssetGroupButton = (
    <div>
      <Button
        testId="createButton"
        label={t('SEARCH_DATA.CREATE')}
        onClick={() => {
          setDisplayAssetGroupEditor(true);
        }}
        size="xs"
        disabled={displayAssetGroupEditor}
        extraClassNames="tw:mr-1"
      />
      {renderSetModeIconButton('assetGroupMoreButton')}
    </div>
  );

  const renderScalingTableButton = (
    <div>
      <Button
        testId="scalingTableCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        size="xs"
        onClick={() => {
          doTrack('Scaling Table Configuration', 'Open Create Item Finder Modal');
          setDisplayTableDefinitionEditor(true);
          resetItemFinder();
        }}
        disabled={displayScalingTableEditor}
        extraClassNames="tw:mr-1"
      />
      {renderSetModeIconButton('scalingMoreButton')}
    </div>
  );

  const renderItemTableButton = (
    <div>
      <Button
        testId="itemTableCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        onClick={() => {
          doTrack('Item Table Configuration', 'Open Create Item Finder');
          setDisplayItemTableEditor(true);
          resetItemFinder();
        }}
        size="sm"
        disabled={displayItemTableView}
        extraClassNames=" pr5 mr5"
      />
      {renderSetModeIconButton('itemTablesMoreButton')}
    </div>
  );

  const renderModelButton = (
    <div>
      <CreateModelButton />
      {renderSetModeIconButton('modelsMoreButton')}
    </div>
  );

  const renderHierarchyButton = (
    <div>
      <Button
        testId="hierarchyCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        size="sm"
        onClick={async () => {
          doTrack('Asset Builder', 'Create Hierarchy', { itemTableId: selectedItemTable?.id });
          await editTableDefinition(selectedItemTable!, 'ITEM');
          setControlPane('ITEM', CONTROL_PANE_DISPLAY.HIERARCHY_CONTROL);
          setEnableTreeCreation('ITEM', true);
        }}
        disabled={false}
        extraClassNames=" pr5 mr5"
      />
      {renderSetModeIconButton('hierarchyMoreButton')}
    </div>
  );

  const renderPreviewBadge = <span className="ml7 preview-badge text-interactive">{t('PREVIEW')}</span>;

  const renderButton = () => {
    if (showCreateAssetGroupButton) {
      return renderAssetGroupButton;
    }
    if (showCreateScalingTableButton) {
      return renderScalingTableButton;
    }
    if (showItemTableButton) {
      return renderItemTableButton;
    }
    if (showCreateModelButton) {
      return renderModelButton;
    }
    if (showCreateHierarchyButton) {
      return renderHierarchyButton;
    }
    return renderSetModeIconButton();
  };

  const nonClickableHeading = (
    <span
      data-testid="nonClickableHeading"
      className="flexColumnContainer flexSpaceBetween flexAlignCenter heading text-interactive fs15">
      <span>
        {t(`SEARCH_DATA.${label}`)}
        {showPreviewBadge && renderPreviewBadge}
      </span>
      {showCreateAssetGroupButton && renderAssetGroupButton}
      {showCreateScalingTableButton && renderScalingTableButton}
      {showItemTableButton && renderItemTableButton}
      {showCreateModelButton && renderModelButton}
      {showCreateHierarchyButton && renderHierarchyButton}
    </span>
  );

  const loadingSpinner = (
    <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
      <IconWithSpinner spinning={true} large={true} />
      <span className="mb5 ml5 mt5">{t(`SEARCH_DATA.LOADING_${label}`)}</span>
    </div>
  );

  const isLoadingHierarchies = loadStatus === SEARCH_RESULT_TYPES.ASSETS && mode === 'item-hierarchies';

  return (
    <div>
      {loadStatus === LOAD_STATUS.LOADING && !searchingUsages && (
        <div data-testid="loadingStatus">
          {nonClickableHeading}
          {loadingSpinner}
        </div>
      )}

      {loadStatus === LOAD_STATUS.ERROR && !searchingUsages && (
        <div data-testid="errorStatus">
          {nonClickableHeading}
          <div className="flexColumnContainer sq-text-danger cursorPointer pt3 pb6" onClick={onErrorClick}>
            <Icon icon="fa-exclamation-triangle" type="danger" extraClassNames="flexCenter pl5 pr10 pt2" />
            <div className="flexFill pl5">
              {t(`SEARCH_DATA.LOAD_${label}_FAILED`)}
              <br />
              <span className="text-underline">{t('SEARCH_DATA.RETRY')}</span>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 &&
        ((mode === searchMode && !searchingUsages) || mode === 'overview') &&
        noItemsMessage &&
        pane === 'main' && (
          <div>
            {nonClickableHeading}
            {isLoadingHierarchies ? loadingSpinner : <div className="m5">{t(noItemsMessage)}</div>}
          </div>
        )}

      {items.length > 0 && (mode === searchMode || mode === 'overview') && (
        <div>
          {mode === 'overview' && (
            <div data-testid="modeSet" className="flexColumnContainer flexSpaceBetween flexAlignCenter heading">
              {showHomeIcon && (
                <div data-testid="homeIconContainer">
                  <Icon
                    icon="fa-house-chimney"
                    extraClassNames="pl5 pr2"
                    testId="breadcrumbHomeIcon"
                    onClick={onHomeIconClick}
                  />
                  <span className="pr2">»</span>
                </div>
              )}
              <span className="text-interactive flexFill fs15" onClick={() => setModeClick()}>
                {t(`SEARCH_DATA.${label}`)} {totalItemsCount > 0 && `(${totalItemsCount})`}
                {showPreviewBadge && renderPreviewBadge}
              </span>

              {renderButton()}
            </div>
          )}
          {mode === 'item-hierarchies' && displayHierarchyView && <>{nonClickableHeading}</>}

          {_.map(items, (item: any) => (
            <SearchResult
              isSelectingAsset={isSelectingAsset}
              searchTypes={predefinedSearchTypes}
              key={item.id}
              item={item}
              items={items}
              searchResultIcons={searchResultIcons}
              itemSelected={_.indexOf(selectedItemIds, item.id) > -1}
              onClickItem={() => {
                clearSelection(); // unselect selected items
                onClickItem(item);
              }}
              isSelected={indicateSelection && selectedItemId === item.id}
            />
          ))}
        </div>
      )}
      {mode === 'overview' && totalItemsCount > LIMIT_IN_OVERVIEW && (
        <Button
          testId="viewMoreButton"
          label={t('SEARCH_DATA.VIEW_MORE')}
          onClick={() => setModeClick()}
          extraClassNames="viewMoreLinkButton"
        />
      )}
    </div>
  );
};
